import {
  executeGraphqlRequest,
  readCachedGraphqlQuery,
} from '../apollo/graphql-request';

const CHAT_GRAPHQL_ENABLED =
  process.env.REACT_APP_CHAT_GRAPHQL_ENABLED === 'true';

export interface ChatAccountProfile {
  uuid: string;
  firstName?: string | null;
  lastName?: string | null;
  title: string;
  avatarUrl?: string | null;
}

export interface ChatAccount {
  uuid: string;
  username: string;
  profiles: ChatAccountProfile[];
}

export interface ConversationMessage {
  uuid: string;
  body: string;
  createdAt: string;
  isRead: boolean;
  senderAccountUuid: string;
}

export interface ChatConversation {
  uuid: string;
  type: 'DIRECT' | 'EVENT' | 'POSITION';
  ownerAccountUuid: string;
  participantAccountUuid: string;
  hasUnreadMessages: boolean;
  profileUuid?: string | null;
  positionUuid?: string | null;
  eventUuid?: string | null;
  updatedAt: string;
  ownerAccount: ChatAccount;
  participantAccount: ChatAccount;
  position?: {
    uuid: string;
    title: string;
  } | null;
  event?: {
    uuid: string;
    title: string;
  } | null;
  lastMessage?: ConversationMessage | null;
}

const CHAT_ACCOUNT_SELECTION_SET = `
  uuid
  username
  profiles {
    uuid
    firstName
    lastName
    title
    avatarUrl
  }
`;

const CONVERSATION_MESSAGE_SELECTION_SET = `
  uuid
  body
  createdAt
  isRead
  senderAccountUuid
`;

const CHAT_CONVERSATION_SELECTION_SET = `
  uuid
  type
  ownerAccountUuid
  participantAccountUuid
  hasUnreadMessages
  profileUuid
  positionUuid
  eventUuid
  updatedAt
  ownerAccount {
    ${CHAT_ACCOUNT_SELECTION_SET}
  }
  participantAccount {
    ${CHAT_ACCOUNT_SELECTION_SET}
  }
  position {
    uuid
    title
  }
  event {
    uuid
    title
  }
  lastMessage {
    ${CONVERSATION_MESSAGE_SELECTION_SET}
  }
`;

const ACCOUNT_CONVERSATIONS_QUERY = `
  query AccountConversations($accountUuid: ID!) {
    accountConversations(accountUuid: $accountUuid) {
      ${CHAT_CONVERSATION_SELECTION_SET}
    }
  }
`;

const CONVERSATION_QUERY = `
  query Conversation($uuid: ID!) {
    conversation(uuid: $uuid) {
      ${CHAT_CONVERSATION_SELECTION_SET}
    }
  }
`;

const CONVERSATION_MESSAGES_QUERY = `
  query ConversationMessages($conversationUuid: ID!) {
    conversationMessages(conversationUuid: $conversationUuid) {
      ${CONVERSATION_MESSAGE_SELECTION_SET}
    }
  }
`;

const CREATE_DIRECT_CONVERSATION_MUTATION = `
  mutation CreateDirectConversation($input: CreateDirectConversationInput!) {
    createDirectConversation(input: $input) {
      ${CHAT_CONVERSATION_SELECTION_SET}
    }
  }
`;

const SEND_CONVERSATION_MESSAGE_MUTATION = `
  mutation SendConversationMessage($input: SendConversationMessageInput!) {
    sendConversationMessage(input: $input) {
      ${CONVERSATION_MESSAGE_SELECTION_SET}
    }
  }
`;

const MARK_CONVERSATION_MESSAGES_READ_MUTATION = `
  mutation MarkConversationMessagesRead($input: MarkConversationMessagesReadInput!) {
    markConversationMessagesRead(input: $input) {
      ${CONVERSATION_MESSAGE_SELECTION_SET}
    }
  }
`;

async function executeChatRequest<TData>(
  query: string,
  variables: Record<string, unknown>,
  accessToken?: string
): Promise<TData> {
  return executeGraphqlRequest<TData>(query, variables, {
    accessToken,
    requestName: 'Chat',
  });
}

export async function fetchAccountConversations(
  accountUuid: string,
  accessToken?: string
): Promise<ChatConversation[]> {
  if (!CHAT_GRAPHQL_ENABLED) {
    return [];
  }

  const response = await executeChatRequest<{
    accountConversations: ChatConversation[];
  }>(
    ACCOUNT_CONVERSATIONS_QUERY,
    {
      accountUuid,
    },
    accessToken
  );

  return response.accountConversations ?? [];
}

export function readCachedAccountConversations(
  accountUuid: string
): ChatConversation[] | undefined {
  if (!CHAT_GRAPHQL_ENABLED) {
    return [];
  }

  const cachedData = readCachedGraphqlQuery<{
    accountConversations: ChatConversation[];
  }>(ACCOUNT_CONVERSATIONS_QUERY, {
    accountUuid,
  });

  return cachedData?.accountConversations ?? (cachedData ? [] : undefined);
}

export async function fetchConversation(
  uuid: string,
  accessToken?: string
): Promise<ChatConversation | null> {
  if (!CHAT_GRAPHQL_ENABLED) {
    return null;
  }

  const response = await executeChatRequest<{
    conversation?: ChatConversation | null;
  }>(
    CONVERSATION_QUERY,
    {
      uuid,
    },
    accessToken
  );

  return response.conversation ?? null;
}

export function readCachedConversation(
  uuid: string
): ChatConversation | null | undefined {
  if (!CHAT_GRAPHQL_ENABLED) {
    return null;
  }

  const cachedData = readCachedGraphqlQuery<{
    conversation?: ChatConversation | null;
  }>(CONVERSATION_QUERY, {
    uuid,
  });

  if (!cachedData) {
    return undefined;
  }

  return cachedData.conversation ?? null;
}

export async function fetchConversationMessages(
  conversationUuid: string,
  accessToken?: string
): Promise<ConversationMessage[]> {
  if (!CHAT_GRAPHQL_ENABLED) {
    return [];
  }

  const response = await executeChatRequest<{
    conversationMessages: ConversationMessage[];
  }>(
    CONVERSATION_MESSAGES_QUERY,
    {
      conversationUuid,
    },
    accessToken
  );

  return response.conversationMessages ?? [];
}

export function readCachedConversationMessages(
  conversationUuid: string
): ConversationMessage[] | undefined {
  if (!CHAT_GRAPHQL_ENABLED) {
    return [];
  }

  const cachedData = readCachedGraphqlQuery<{
    conversationMessages: ConversationMessage[];
  }>(CONVERSATION_MESSAGES_QUERY, {
    conversationUuid,
  });

  return cachedData?.conversationMessages ?? (cachedData ? [] : undefined);
}

export async function createDirectConversationRequest(
  username: string,
  accessToken?: string
): Promise<ChatConversation> {
  if (!CHAT_GRAPHQL_ENABLED) {
    throw new Error('Chat GraphQL is not available in this API environment.');
  }

  const response = await executeChatRequest<{
    createDirectConversation: ChatConversation;
  }>(
    CREATE_DIRECT_CONVERSATION_MUTATION,
    {
      input: {
        username: username.trim(),
      },
    },
    accessToken
  );

  return response.createDirectConversation;
}

export async function sendConversationMessageRequest(
  conversationUuid: string,
  body: string,
  accessToken?: string
): Promise<ConversationMessage> {
  if (!CHAT_GRAPHQL_ENABLED) {
    throw new Error('Chat GraphQL is not available in this API environment.');
  }

  const response = await executeChatRequest<{
    sendConversationMessage: ConversationMessage;
  }>(
    SEND_CONVERSATION_MESSAGE_MUTATION,
    {
      input: {
        conversationUuid,
        body: body.trim(),
      },
    },
    accessToken
  );

  return response.sendConversationMessage;
}

export async function markConversationMessagesReadRequest(
  conversationUuid: string,
  accessToken?: string
): Promise<ConversationMessage[]> {
  if (!CHAT_GRAPHQL_ENABLED) {
    return [];
  }

  const response = await executeChatRequest<{
    markConversationMessagesRead: ConversationMessage[];
  }>(
    MARK_CONVERSATION_MESSAGES_READ_MUTATION,
    {
      input: {
        conversationUuid,
      },
    },
    accessToken
  );

  return response.markConversationMessagesRead ?? [];
}
